import React, { Fragment, useState, useEffect } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Row, Col, Card, Table, Badge, Button, Spinner, Dropdown, Form } from "react-bootstrap";
import SignatureModal from "../Common/SignatureModal";
import VerifiedCopyActions from "../Common/VerifiedCopyActions";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";

const ContractualWorkOrderList = () => {
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const workOrderFromUrl = searchParams.get("workOrder") || "";
  const projectFromUrl = searchParams.get("project") || "";

 const [cwos, setCwos] = useState([]);
 const [loading, setLoading] = useState(true);
 const [pdfLoading, setPdfLoading] = useState(null);
 const [previewingPdf, setPreviewingPdf] = useState(null);
 const [showSignModal, setShowSignModal] = useState(false);
 const [selectedCWO, setSelectedCWO] = useState(null);
 const [statusToUpdate, setStatusToUpdate] = useState("");

 const [filters, setFilters] = usePersistentFilters("contractual_work_orders_filters", {
 search: "",
 status: "",
 fromDate: "",
 toDate: ""
 });

 useEffect(() => {
 const delayDebounceFn = setTimeout(() => {
 fetchCWOs();
 }, 500);
 return () => clearTimeout(delayDebounceFn);
  }, [workOrderFromUrl, projectFromUrl, filters.status, filters.fromDate, filters.toDate, filters.search]);

 const fetchCWOs = async () => {
 try {
 setLoading(true);
 let url = "/contractual-work-orders?";
 if (workOrderFromUrl) url += `workOrder=${workOrderFromUrl}&`;
  if (projectFromUrl) url += `project=${projectFromUrl}&`;
 if (filters.status) url += `status=${filters.status}&`;
 if (filters.fromDate) url += `fromDate=${filters.fromDate}&`;
 if (filters.toDate) url += `toDate=${filters.toDate}&`;
 if (filters.search) url += `search=${filters.search}&`;

 const res = await api.get(url);
 if (res.data.success) setCwos(res.data.data);
 } catch (err) {
 console.error(err);
 } finally {
 setLoading(false);
 }
 };

 const handleUpdateStatus = (id, status) => {
 setSelectedCWO(id);
 setStatusToUpdate(status);
 if (status === "Approved") {
 setShowSignModal(true);
 } else {
 confirmStatusUpdate(id, status);
 }
 };

 const confirmStatusUpdate = async (id, status, signFile = null) => {
 try {
 const result = await Swal.fire({
 title: "Are you sure?",
 text: `Do you want to ${status.toLowerCase()} this work order?`,
 icon: "warning",
 showCancelButton: true,
 confirmButtonText: "Yes",
 });

 if (result.isConfirmed) {
 let payload = { status };
 if (signFile) {
 const reader = new FileReader();
 reader.readAsDataURL(signFile);
 reader.onloadend = async () => {
 payload.sign = reader.result;
 payload.field = "verifiedBySign";
 await api.patch(`/contractual-work-orders/${id}/status`, payload);
 Swal.fire("Success", `Work Order ${status}`, "success");
 fetchCWOs();
 setShowSignModal(false);
 };
 } else {
 await api.patch(`/contractual-work-orders/${id}/status`, payload);
 Swal.fire("Success", `Work Order ${status}`, "success");
 fetchCWOs();
 }
 }
 } catch (err) {
 Swal.fire("Error", "Failed to update status", "error");
 }
 };

 const stats = {
 total: cwos.length,
 approved: cwos.filter(c => c.status === "Approved").length,
 pending: cwos.filter(c => c.status === "Pending").length,
 value: cwos.reduce((sum, c) => sum + (c.totalAmount || 0), 0)
 };

 const handleDelete = async (id) => {
 const result = await Swal.fire({
 title: "Are you sure?",
 text: "Delete this Contractual WO?",
 icon: "warning",
 showCancelButton: true,
 confirmButtonText: "Yes, delete",
 });
 if (result.isConfirmed) {
 try {
 await api.delete(`/contractual-work-orders/${id}`);
 fetchCWOs();
 Swal.fire("Deleted", "", "success");
 } catch (err) {
 Swal.fire("Error", "Failed to delete", "error");
 }
 }
 };

 const statusVariant = (status) => {
 switch (status) {
 case "Approved": return "success";
 case "Rejected": return "danger";
 case "In Progress": return "warning";
 case "Completed": return "info";
 default: return "secondary";
 }
 };

 const handlePreviewPDF = async (id) => {
 try {
 setPreviewingPdf(id);
 const token = localStorage.getItem("token");
 const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
 const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
 const url = `${cleanBaseUrl}/api/contractual-work-orders/download-pdf/${id}`;

 const response = await fetch(url, {
 method: 'GET',
 headers: {
 'Authorization': `Bearer ${token}`,
 'Accept': 'application/pdf'
 }
 });

 if (!response.ok) throw new Error("Failed to generate PDF");

 const blob = await response.blob();
 const fileURL = window.URL.createObjectURL(
 new Blob([blob], { type: "application/pdf" })
 );

 window.open(fileURL, "_blank");
 } catch (err) {
 console.error("PDF preview error:", err);
 Swal.fire("Error", "Failed to preview PDF", "error");
 } finally {
 setPreviewingPdf(null);
 }
 };

 const handleDownloadPdf = async (id) => {
 try {
 setPdfLoading(id);
 const token = localStorage.getItem("token");
 const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
 const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
 const url = `${cleanBaseUrl}/api/contractual-work-orders/download-pdf/${id}`;

 const response = await fetch(url, {
 method: 'GET',
 headers: {
 'Authorization': `Bearer ${token}`,
 'Accept': 'application/pdf'
 }
 });

 if (!response.ok) throw new Error("Failed to generate PDF");

 const blob = await response.blob();
 const downloadUrl = window.URL.createObjectURL(blob);
 const link = document.createElement('a');
 link.href = downloadUrl;
 link.setAttribute('download', `CWO_${new Date().getTime()}.pdf`);
 document.body.appendChild(link);
 link.click();
 link.remove();
 window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 3000);
 } catch (err) {
 console.error(err);
 Swal.fire("Error", "Failed to download PDF", "error");
 } finally {
 setPdfLoading(null);
 }
 };

 return (
 <Fragment>
 <PageTitle activeMenu="Contractual WO" motherMenu="Accounts" />
 
 <Row>
 <Col lg={12}>
 <Card className="border-0 shadow-sm overflow-visible">
 <Card.Header className="bg-white border-bottom py-3 d-flex flex-wrap justify-content-between align-items-center">
 <Card.Title className="mb-0 text-primary fw-bold">Contractual Work Orders</Card.Title>
 <div className="d-flex flex-wrap gap-2">
 <div className="input-group input-group-sm" style={{ width: '200px' }}>
 <Form.Control 
 size="sm"
 placeholder="Search..." 
 value={filters.search}
 onChange={(e) => setFilters({...filters, search: e.target.value})}
 />
 </div>
 <Form.Control 
 type="date" 
 size="sm" 
 style={{ width: '130px' }}
 value={filters.fromDate}
 onChange={(e) => setFilters({...filters, fromDate: e.target.value})}
 />
 <Form.Control 
 type="date" 
 size="sm" 
 style={{ width: '130px' }}
 value={filters.toDate}
 onChange={(e) => setFilters({...filters, toDate: e.target.value})}
 />
 <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>Back</Button>
 <Button variant="primary" size="sm" onClick={() => navigate(`/contractual-debit/add?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`)}>
 <i className="fa fa-plus me-2"></i>Create New
 </Button>
 </div>
 </Card.Header>

 <Card.Body className="p-0 overflow-visible">
 {loading ? (
 <div className="text-center py-5">
 <div className="spinner-border text-primary"></div>
 <p className="mt-3 text-muted">Loading...</p>
 </div>
 ) : (
 <div className="table-responsive overflow-visible">
 <Table hover className="mb-0 overflow-visible text-center align-middle">
 <thead className="bg-light small fw-bold">
 <tr>
 <th className="py-3">CWO No / Date</th>
 <th className="py-3">Main Work Order</th>
 <th className="py-3">Vendor</th>
 <th className="py-3">Created By</th>
 <th className="py-3">Amount</th>
 <th className="py-3 text-center">Status</th>
 <th className="py-3 text-center">Actions</th>
 </tr>
 </thead>
 <tbody>
 {cwos.map((item) => (
 <tr key={item._id} className="align-middle">
 <td>
 <div className="fw-bold">{item.cwoNo}</div>
 <small className="text-muted">{new Date(item.createdAt).toLocaleDateString('en-IN')}</small>
 </td>
 <td>{item.workOrder?.woNo || "—"}</td>
 <td>{item.vendor?.name || "—"}</td>
 <td>{item.createdBy?.name || "System"}</td>
 <td>
 <div className="fw-bold">₹{item.totalAmount?.toLocaleString()}</div>
 </td>
 <td className="text-center">
 <Badge bg={statusVariant(item.status)}>{item.status}</Badge>
 </td>
 <td className="overflow-visible">
 <div className="d-flex justify-content-center align-items-center gap-2 flex-wrap">
 <VerifiedCopyActions
 id={item._id}
 type="contractual-work-orders"
 moduleKey="contractualwo"
 hasVerifiedCopy={Boolean(item.verifiedCopy?.filePath || item.verifiedCopy?.fileUrl)}
 verifiedCopy={item.verifiedCopy}
 onUploadSuccess={fetchCWOs}
 />
 <Dropdown align="end">
 <Dropdown.Toggle variant="light" size="sm" className="arrow-none">
 <i className="fa fa-ellipsis-v"></i>
 </Dropdown.Toggle>
 <Dropdown.Menu style={{ zIndex: 1050 }}>
 <Dropdown.Item onClick={() => navigate(`/contractual-debit/edit/${item._id}${projectFromUrl ? `?project=${projectFromUrl}` : ""}`)}>
 <i className="fa fa-edit me-2 text-primary"></i> Edit
 </Dropdown.Item>
 {item.status === "Pending" && (
 <>
 <Dropdown.Item onClick={() => handleUpdateStatus(item._id, "Approved")}>
 <i className="fa fa-check me-2 text-success"></i> Approve
 </Dropdown.Item>
 <Dropdown.Item onClick={() => handleUpdateStatus(item._id, "Rejected")}>
 <i className="fa fa-times me-2 text-danger"></i> Reject
 </Dropdown.Item>
 </>
 )}
 {item.status === "Approved" && (
 <>
 <Dropdown.Item
 onClick={() =>
 navigate(`/contractor-billing/add?cwo=${item._id}&workOrder=${item.workOrder?._id || item.workOrder}`)
 }
 >
 <i className="fa fa-file-invoice me-2 text-info"></i> Contractor Billing
 </Dropdown.Item>
 <Dropdown.Item
 onClick={() =>
 navigate(`/contractor-jmr/add?contractualWorkOrder=${item._id}`)
 }
 >
 <i className="fas fa-ruler-combined me-2 text-success"></i> Create JMR
 </Dropdown.Item>
 </>
 )}
 <Dropdown.Item onClick={() => handlePreviewPDF(item._id)} disabled={previewingPdf === item._id}>
 <i className="fa fa-eye me-2 text-info"></i> Preview PDF
 </Dropdown.Item>
 <Dropdown.Item onClick={() => handleDownloadPdf(item._id)} disabled={pdfLoading === item._id}>
 <i className="fa fa-file-pdf me-2 text-danger"></i> Download PDF
 </Dropdown.Item>
 <Dropdown.Item onClick={() => handleDelete(item._id)} className="text-danger">
 <i className="fa fa-trash me-2"></i> Delete
 </Dropdown.Item>
 </Dropdown.Menu>
 </Dropdown>
 </div>
 </td>
 </tr>
 ))}
 {cwos.length === 0 && (
 <tr>
 <td colSpan="7" className="text-center text-muted py-5">No records found.</td>
 </tr>
 )}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <SignatureModal
 show={showSignModal}
 onHide={() => setShowSignModal(false)}
 onSave={(file) => confirmStatusUpdate(selectedCWO, statusToUpdate, file)}
 title="Verified By — Approval Signature"
 />

 {/* PDF Loader Overlay */}
 {(pdfLoading || previewingPdf) && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{
 background: "rgba(0,0,0,0.4)",
 zIndex: 9999,
 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing CWO PDF…</h6>
 </div>
 </div>
 )}
 </Fragment>
 );
};

export default ContractualWorkOrderList;
